/**
 * TaskPool — Sequential async task queue.
 *
 * Enqueues async tasks and processes them one at a time.
 * If a task is added while another is running, it waits in the queue.
 * Inspired by UDG Poker's TaskPool pattern.
 */

export type Task = () => Promise<void>;

export class TaskPool {
  private queue: Task[] = [];
  private running = false;

  /** Enqueue a task. Starts processing immediately if idle. */
  enqueue(task: Task): void {
    this.queue.push(task);
    if (!this.running) {
      this.flush();
    }
  }

  /** Process queue sequentially. */
  private async flush(): Promise<void> {
    this.running = true;
    while (this.queue.length > 0) {
      const task = this.queue.shift()!;
      try {
        await task();
      } catch (e) {
        console.error('[TaskPool] Task failed:', e);
      }
    }
    this.running = false;
  }

  /** Clear all pending tasks (e.g., on disconnect). */
  clear(): void {
    this.queue.length = 0;
  }

  get pending(): number {
    return this.queue.length;
  }

  get isRunning(): boolean {
    return this.running;
  }
}
